import {
  createMaterialTopTabNavigator,
  MaterialTopTabNavigationEventMap,
  MaterialTopTabNavigationOptions
} from "expo-router/js-top-tabs";
import { TabNavigationState } from "expo-router/react-navigation";
import { router, useSegments, withLayoutContext } from 'expo-router';
import { useState } from 'react';
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Animated, {
  Easing,
  ReduceMotion,
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';

import TabBarLabel from '@/components/TabBarLabel';
import TRBShowBanner from '@/components/trbShow/TRBShowBanner';
import PlatformContainer from '@/components/web/PlatformContainer';
import { ScrollLiistenerContextProvider } from '@/contexts/scrollListenerTabViewContext';
import { useColor } from '@/hooks/useColor';
import { ParamListBase } from '@/types/navigation';
import SearchNpoAutoComplete from '@/components/inputs/SearchNpoAutoComplete';
import { Ionicons } from '@expo/vector-icons';
import HeaderBar from '@/components/HeaderBar';

const { Navigator } = createMaterialTopTabNavigator();
export const MaterialTopTabs = withLayoutContext<
  MaterialTopTabNavigationOptions,
  typeof Navigator,
  TabNavigationState<ParamListBase>,
  MaterialTopTabNavigationEventMap
>(Navigator);

export default function HomeLayout() {
  const color = useColor();
  const { width: screenWidth } = useWindowDimensions();

  const segments = useSegments();
  const activeRouteKey = segments[segments.length - 1];

  // Banner animation
  const [pointerEvents, setPointerEvents] = useState<'none' | 'auto'>('auto');
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const BANNER_HEIGHT = Platform.OS === 'web' ? 115 : 95;
  const SEARCH_MAX_HEIGHT = 100;
  const SEARCH_MARGIN = 20;

  const bannerHeight = useSharedValue(BANNER_HEIGHT);
  const opacity = useSharedValue(1);
  const translateY = useSharedValue(0);
  const zIndex = useSharedValue(0);
  const scrollOffset = useSharedValue(0);

  // Search animation values
  const searchMaxHeight = useSharedValue(SEARCH_MAX_HEIGHT);
  const searchMargin = useSharedValue(SEARCH_MARGIN);
  const searchOpacity = useSharedValue(1);
  const searchTranslateY = useSharedValue(0);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
    zIndex: zIndex.value,
    height: bannerHeight.value
  }));

  const searchAnimatedStyle = useAnimatedStyle(() => ({
    opacity: searchOpacity.value,
    transform: [{ translateY: searchTranslateY.value }],
    maxHeight: searchMaxHeight.value,
    marginVertical: searchMargin.value
  }));

  const show = () => {
    'worklet';
    // Banner
    zIndex.value = 0;
    translateY.value = 0;
    bannerHeight.value = withTiming(BANNER_HEIGHT, {
      duration: 200,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
    opacity.value = withTiming(1, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });

    // Search
    searchTranslateY.value = 0;
    searchMaxHeight.value = withTiming(SEARCH_MAX_HEIGHT, {
      duration: 200,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
    searchOpacity.value = withTiming(1, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
    searchMargin.value = withTiming(SEARCH_MARGIN, {
      duration: 200,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
  };

  const hide = () => {
    'worklet';
    // Banner
    zIndex.value = searchOpen ? 0 : 10;
    opacity.value = withTiming(0, {
      duration: 200,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
    translateY.value = withTiming(-50, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
    bannerHeight.value = withTiming(0, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });

    // Search
    searchOpacity.value = withTiming(0, {
      duration: 200,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
    searchTranslateY.value = withTiming(-20, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
    searchMaxHeight.value = withTiming(0, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
    searchMargin.value = withTiming(0, {
      duration: 300,
      easing: Easing.inOut(Easing.quad),
      reduceMotion: ReduceMotion.System
    });
  };

  useDerivedValue(() => {
    const isHidden = bannerHeight.value < BANNER_HEIGHT;
    runOnJS(setPointerEvents)(isHidden ? 'none' : 'auto');
  });

  const isBannerVisible = useSharedValue(true);

  useAnimatedReaction(
    () => scrollOffset?.value,
    (current: number, previous: number | null) => {
      if (current !== null && previous !== null && current !== previous) {
        if (bannerHeight.value > 1 && bannerHeight.value < BANNER_HEIGHT - 1) {
          return;
        }

        const diff = current - previous;
        const isSignificantDown = diff > 10;
        const isSignificantUp = diff < -10;

        let shouldShow = isBannerVisible.value;

        if (current < 75) {
          shouldShow = true;
        } else if (isSignificantDown) {
          shouldShow = false;
        } else if (isSignificantUp) {
          shouldShow = true;
        }

        if (isBannerVisible.value !== shouldShow) {
          isBannerVisible.value = shouldShow;
          if (shouldShow) {
            show();
          } else {
            hide();
          }
        }
      }
    },
    [searchOpen]
  );

  return (
    <ScrollLiistenerContextProvider
      value={{
        activeRouteKey: activeRouteKey,
        scrollOffset: scrollOffset
      }}>
      <PlatformContainer
        containerCustomClassname="flex flex-col"
        contentCustomClassnames="flex flex-col"
        overflowY={false}>
        <SafeAreaView edges={Platform.OS === 'ios' ? ['top'] : []} style={styles.flexOne}>
          {/* md breakpoint --> 768 px */}
          {(Platform.OS !== 'web' || screenWidth < 768) && (
            <View style={styles.zIndex}>
              <HeaderBar
                showSearch={false}
                showBSText={Platform.OS !== 'web'}
                exitingLeft={Platform.OS !== 'web'}
                onSearchStateChange={(isSearching) => {
                  setSearchOpen(isSearching);
                }}
              />
            </View>
          )}
          <Animated.View pointerEvents={pointerEvents} style={[styles.showBannerContainer, animatedStyle]}>
            <TRBShowBanner style={Platform.OS === 'web' && styles.showBannerWeb} />
          </Animated.View>

          {Platform.OS === 'web' ? (
            <View style={[styles.searchBarContainer, { maxHeight: SEARCH_MAX_HEIGHT, marginVertical: SEARCH_MARGIN }]}>
              <SearchNpoAutoComplete
                leadingIcon={<Ionicons size={24} name="search" style={styles.searchIcon} />}
                variant="rounded_with_shadow"
                onPredictionTap={({ value, label }) => {
                  router.navigate({
                    pathname: `/npoDetail/[npoId]/insights`,
                    params: {
                      npoId: value,
                      name: label
                    }
                  });
                }}
              />
            </View>
          ) : (
            <Animated.View pointerEvents={pointerEvents} style={[styles.searchBarContainer, searchAnimatedStyle]}>
              <SearchNpoAutoComplete
                leadingIcon={<Ionicons size={24} name="search" style={styles.searchIcon} />}
                variant="rounded_with_shadow"
                onPredictionTap={({ value, label }) => {
                  router.navigate({
                    pathname: `/npoDetail/[npoId]/insights`,
                    params: {
                      npoId: value,
                      name: label
                    }
                  });
                }}
              />
            </Animated.View>
          )}

          <MaterialTopTabs
            screenOptions={{
              tabBarContentContainerStyle: {
                backgroundColor: color.background
              },
              tabBarIndicatorStyle: { display: 'none' },
              tabBarStyle: styles.tabBarStyle,
              swipeEnabled: false
            }}>
            <MaterialTopTabs.Screen
              name="npoTabs"
              options={{
                tabBarLabel: ({ focused }: { focused: boolean }) => (
                  <TabBarLabel focused={focused} side="left" text={'home.npos'} />
                )
              }}
            />
            <MaterialTopTabs.Screen
              name="missions"
              options={{
                tabBarLabel: ({ focused }: { focused: boolean }) => (
                  <TabBarLabel focused={focused} side="right" text={'home.missions'} />
                )
              }}
            />
          </MaterialTopTabs>
        </SafeAreaView>
      </PlatformContainer>
    </ScrollLiistenerContextProvider>
  );
}

const styles = StyleSheet.create({
  tabBarStyle: {
    shadowOpacity: 0,
    shadowColor: 'transparent',
    width: 290,
    alignSelf: 'center'
  },

  flexOne: {
    flex: 1
  },

  zIndex: {
    zIndex: 99
  },

  showBannerContainer: {
    overflow: 'visible'
  },

  searchBarContainer: {
    paddingHorizontal: 16,
    zIndex: 99
  },

  showBannerWeb: {
    marginTop: 15
  },

  searchIcon: {
    color: '#808080'
  }
});
